import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import api from '../axios';
import ModuleHeader from '../components/ModuleHeader';
import Footer from '../components/Footer';

const GestaoConsolidadores = ({ onLogout }) => {
  const [consolidadores, setConsolidadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroAtivo, setFiltroAtivo] = useState('todos');
  const [filtroDisponivel, setFiltroDisponivel] = useState('todos');
  const [filtroSexo, setFiltroSexo] = useState('todos');
  const [dialogEdit, setDialogEdit] = useState(false);
  const [dialogDelete, setDialogDelete] = useState(false);
  const [consolidadorSelecionado, setConsolidadorSelecionado] = useState(null);
  const [formData, setFormData] = useState({});
  const [isNovo, setIsNovo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    ativos: 0,
    inativos: 0,
    disponiveis: 0,
    masculinos: 0,
    femininos: 0
  });

  useEffect(() => {
    fetchConsolidadores();
  }, []);

  const fetchConsolidadores = async () => {
    try {
      const response = await api.get('/consolidadores/');
      setConsolidadores(response.data);
      calcularStats(response.data);
    } catch (error) {
      console.error('Erro ao buscar consolidadores:', error);
      setError('Erro ao carregar consolidadores');
    } finally {
      setLoading(false);
    }
  };

  const calcularStats = (dados) => {
    const total = dados.length;
    const ativos = dados.filter(c => c.ativo).length;
    const inativos = total - ativos;
    const disponiveis = dados.filter(c => c.disponivel_contato && c.ativo).length;
    const masculinos = dados.filter(c => c.sexo === 'masculino').length;
    const femininos = dados.filter(c => c.sexo === 'feminino').length;
    
    setStats({ total, ativos, inativos, disponiveis, masculinos, femininos });
  };

  const consolidadoresFiltrados = consolidadores.filter(c => {
    if (filtroAtivo === 'ativos' && !c.ativo) return false;
    if (filtroAtivo === 'inativos' && c.ativo) return false;
    if (filtroDisponivel === 'disponiveis' && !c.disponivel_contato) return false;
    if (filtroDisponivel === 'indisponiveis' && c.disponivel_contato) return false;
    if (filtroSexo !== 'todos' && c.sexo !== filtroSexo) return false;
    return true;
  });

  const handleToggleAtivo = async (consolidador) => {
    try {
      await api.put(`/consolidadores/${consolidador.id}`, {
        ...consolidador,
        ativo: !consolidador.ativo
      });
      
      setSuccess(`Consolidador ${consolidador.ativo ? 'desativado' : 'ativado'} com sucesso`);
      fetchConsolidadores();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const handleToggleDisponivel = async (consolidador) => {
    try {
      await api.put(`/consolidadores/${consolidador.id}`, {
        ...consolidador,
        disponivel_contato: !consolidador.disponivel_contato
      });
      
      setSuccess(`Disponibilidade atualizada com sucesso`);
      fetchConsolidadores();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao atualizar disponibilidade');
    }
  };

  const handleAbrirDialogEdit = (consolidador) => {
    setConsolidadorSelecionado(consolidador);
    setIsNovo(false);
    setFormData(consolidador);
    setDialogEdit(true);
  };

  const handleAbrirNovo = () => {
    setConsolidadorSelecionado(null);
    setIsNovo(true);
    setFormData({});
    setDialogEdit(true);
  };

  const handleSalvarEdicao = async () => {
    try {
      if (isNovo) {
        await api.post('/consolidadores/', formData);
        setSuccess('Consolidador cadastrado com sucesso');
      } else {
        await api.put(`/consolidadores/${consolidadorSelecionado.id}`, formData);
        setSuccess('Consolidador atualizado com sucesso');
      }
      setDialogEdit(false);
      fetchConsolidadores();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao atualizar consolidador');
    }
  };

  const handleAbrirDialogDelete = (consolidador) => {
    setConsolidadorSelecionado(consolidador);
    setDialogDelete(true);
  };

  const handleDeletar = async () => {
    try {
      await api.delete(`/consolidadores/${consolidadorSelecionado.id}`);
      
      setSuccess('Consolidador removido com sucesso');
      setDialogDelete(false);
      fetchConsolidadores();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao remover consolidador');
    }
  };

  const handleInputChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    return data.toLocaleDateString('pt-BR');
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <ModuleHeader module="Consolidação" title="Gestão de Consolidadores" />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" color="primary" onClick={handleAbrirNovo}>
            + Novo consolidador
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Cards de Estatísticas */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="caption">
                  Total
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="caption">
                  Ativos
                </Typography>
                <Typography variant="h4" color="success.main" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>
                  {stats.ativos}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="caption">
                  Inativos
                </Typography>
                <Typography variant="h4" color="error.main" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>
                  {stats.inativos}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="caption">
                  Disponíveis
                </Typography>
                <Typography variant="h4" color="primary.main" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>
                  {stats.disponiveis}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="caption">
                  Masculinos
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>{stats.masculinos}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="caption">
                  Femininos
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>{stats.femininos}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                value={filtroAtivo}
                onChange={(e) => setFiltroAtivo(e.target.value)}
                size="small"
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="ativos">Ativos</MenuItem>
                <MenuItem value="inativos">Inativos</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Disponibilidade"
                value={filtroDisponivel}
                onChange={(e) => setFiltroDisponivel(e.target.value)}
                size="small"
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="disponiveis">Disponíveis</MenuItem>
                <MenuItem value="indisponiveis">Indisponíveis</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Sexo"
                value={filtroSexo}
                onChange={(e) => setFiltroSexo(e.target.value)}
                size="small"
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="masculino">Masculino</MenuItem>
                <MenuItem value="feminino">Feminino</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Consolidadores */}
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Sexo</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Disponível</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Cadastro</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="center">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consolidadoresFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                      Nenhum consolidador encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                consolidadoresFiltrados.map((consolidador) => (
                  <TableRow key={consolidador.id} hover>
                    <TableCell>
                      <strong>{consolidador.nome}</strong>
                    </TableCell>
                    <TableCell>{consolidador.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={consolidador.sexo}
                        size="small"
                        color={consolidador.sexo === 'masculino' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>{consolidador.telefone || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={consolidador.ativo ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={consolidador.ativo ? 'success' : 'error'}
                        icon={consolidador.ativo ? <CheckCircleIcon /> : <CancelIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={consolidador.disponivel_contato}
                        onChange={() => handleToggleDisponivel(consolidador)}
                        disabled={!consolidador.ativo}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatarData(consolidador.data_cadastro)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                        <Tooltip title={consolidador.ativo ? 'Desativar' : 'Ativar'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleAtivo(consolidador)}
                            color={consolidador.ativo ? 'error' : 'success'}
                          >
                            {consolidador.ativo ? <PersonOffIcon /> : <PersonAddIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleAbrirDialogEdit(consolidador)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            onClick={() => handleAbrirDialogDelete(consolidador)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog Editar */}
        <Dialog open={dialogEdit} onClose={() => setDialogEdit(false)} maxWidth="md" fullWidth>
          <DialogTitle>{isNovo ? 'Cadastrar Consolidador' : 'Editar Consolidador'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  name="nome"
                  value={formData.nome || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  name="telefone"
                  value={formData.telefone || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WhatsApp"
                  name="telefone_whatsapp"
                  value={formData.telefone_whatsapp || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Data de Nascimento"
                  name="data_nascimento"
                  type="date"
                  value={formData.data_nascimento || ''}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Sexo"
                  name="sexo"
                  value={formData.sexo || ''}
                  onChange={handleInputChange}
                  required
                >
                  <MenuItem value="masculino">Masculino</MenuItem>
                  <MenuItem value="feminino">Feminino</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Endereço"
                  name="endereco"
                  value={formData.endereco || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Bairro"
                  name="bairro"
                  value={formData.bairro || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Cidade"
                  name="cidade"
                  value={formData.cidade || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Estado"
                  name="estado"
                  value={formData.estado || ''}
                  onChange={handleInputChange}
                  inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                  placeholder="UF"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CEP"
                  name="cep"
                  value={formData.cep || ''}
                  onChange={handleInputChange}
                  inputProps={{ maxLength: 9 }}
                  placeholder="00000-000"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%', flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.ativo || false}
                        onChange={handleInputChange}
                        name="ativo"
                        color="success"
                      />
                    }
                    label="Ativo"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.disponivel_contato || false}
                        onChange={handleInputChange}
                        name="disponivel_contato"
                        color="primary"
                      />
                    }
                    label="Disponível"
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogEdit(false)}>Cancelar</Button>
            <Button onClick={handleSalvarEdicao} variant="contained" color="primary">
              {isNovo ? 'Cadastrar' : 'Salvar Alterações'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Deletar */}
        <Dialog open={dialogDelete} onClose={() => setDialogDelete(false)} fullWidth maxWidth="sm">
          <DialogTitle>Remover Consolidador</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja remover <strong>{consolidadorSelecionado?.nome}</strong>?
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Esta ação também removerá o usuário associado e não pode ser desfeita!
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogDelete(false)}>Cancelar</Button>
            <Button onClick={handleDeletar} variant="contained" color="error">
              Remover
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
};

export default GestaoConsolidadores;
